import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../lib/api";
import { Navbar } from "../components/Navbar";
import { MODULE_THEMES } from "../data/modules";
import { Plus, Trash2, Pencil, X, Check } from "lucide-react";
import { toast } from "sonner";

const MODULE_LIST = [
  { key: "couples_counseling", name: "Couples & Marriage" },
  { key: "anger_management", name: "Anger Management" },
  { key: "anxiety", name: "Anxiety" },
  { key: "depression", name: "Depression" },
  { key: "bipolar", name: "Bipolar Support" },
  { key: "adhd", name: "ADHD Focus" },
  { key: "autism_spectrum", name: "Autism Spectrum" },
  { key: "ptsd", name: "PTSD & Trauma" },
  { key: "ocd", name: "OCD" },
  { key: "substance_recovery", name: "Substance Recovery" },
  { key: "schizophrenia", name: "Schizophrenia Support" },
  { key: "borderline_pd", name: "Borderline Personality" },
  { key: "narcissistic_pd", name: "Narcissistic Traits" },
  { key: "grief", name: "Grief & Loss" },
  { key: "self_esteem", name: "Self-Esteem" },
  { key: "abandonment", name: "Abandonment" },
  { key: "eating_disorder", name: "Eating Disorder Support" },
  { key: "people_pleasing", name: "People-Pleasing" },
];

const emptyForm = () => ({
  module: "couples_counseling",
  text: "",
  options: [
    { id: "a", text: "" },
    { id: "b", text: "" },
    { id: "c", text: "" },
    { id: "d", text: "" },
  ],
  correct_ids: [],
  explanation: "",
  points: 10,
});

export default function Admin() {
  const [filter, setFilter] = useState("");
  const [questions, setQuestions] = useState([]);
  const [form, setForm] = useState(null);
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(() => {
    const url = filter ? `/admin/questions?module=${filter}` : "/admin/questions";
    api.get(url).then((r) => setQuestions(r.data));
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => {
    setEditingId(null);
    setForm(emptyForm());
  };

  const openEdit = (q) => {
    setEditingId(q.id);
    setForm({
      module: q.module,
      text: q.text,
      options: q.options,
      correct_ids: q.correct_ids,
      explanation: q.explanation || "",
      points: q.points || 10,
    });
  };

  const toggleCorrect = (id) => {
    setForm((f) => ({
      ...f,
      correct_ids: f.correct_ids.includes(id)
        ? f.correct_ids.filter((x) => x !== id)
        : [...f.correct_ids, id],
    }));
  };

  const save = async () => {
    const payload = {
      ...form,
      options: form.options.filter((o) => o.text.trim()),
    };
    if (!payload.text.trim()) return toast.error("Question text is required");
    if (payload.options.length < 2) return toast.error("Add at least two options");
    if (payload.correct_ids.length === 0) return toast.error("Mark at least one correct answer");
    try {
      if (editingId) {
        await api.put(`/admin/questions/${editingId}`, payload);
        toast.success("Question updated");
      } else {
        await api.post("/admin/questions", payload);
        toast.success("Question added");
      }
      setForm(null);
      setEditingId(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this question?")) return;
    await api.delete(`/admin/questions/${id}`);
    toast.success("Question deleted");
    load();
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-5xl mx-auto px-5 py-10">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-head font-extrabold text-3xl tracking-tight">Question manager</h1>
            <p className="text-muted-foreground mt-1">Add and edit interventions for each program.</p>
          </div>
          <button
            onClick={openNew}
            className="flex items-center gap-2 bg-[#2D3748] text-white font-semibold px-6 py-3 rounded-full hover:-translate-y-0.5 transition-transform"
            data-testid="admin-new-question"
          >
            <Plus className="w-4 h-4" /> New question
          </button>
        </div>

        <div className="flex gap-2 flex-wrap mt-6">
          <FilterChip active={filter === ""} onClick={() => setFilter("")} label="All" />
          {MODULE_LIST.map((m) => (
            <FilterChip
              key={m.key}
              active={filter === m.key}
              onClick={() => setFilter(m.key)}
              label={m.name}
              color={MODULE_THEMES[m.key].primary}
            />
          ))}
        </div>

        <div className="mt-6 space-y-3">
          {questions.map((q) => {
            const t = MODULE_THEMES[q.module];
            return (
              <div
                key={q.id}
                className="bg-white rounded-3xl p-6 shadow-sm border border-black/5"
                data-testid={`admin-question-${q.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <span
                      className="inline-block text-xs font-bold px-3 py-1 rounded-full mb-2"
                      style={{ backgroundColor: t.bg, color: t.primary }}
                    >
                      {MODULE_LIST.find((m) => m.key === q.module)?.name} · {q.points} pts
                    </span>
                    <p className="font-semibold">{q.text}</p>
                    <ul className="mt-2 space-y-1">
                      {q.options.map((o) => (
                        <li
                          key={o.id}
                          className={`text-sm flex items-center gap-2 ${
                            q.correct_ids.includes(o.id)
                              ? "text-[#2F855A] font-semibold"
                              : "text-muted-foreground"
                          }`}
                        >
                          {q.correct_ids.includes(o.id) ? (
                            <Check className="w-3.5 h-3.5" />
                          ) : (
                            <span className="w-3.5" />
                          )}
                          {o.text}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button
                      onClick={() => openEdit(q)}
                      className="p-2 rounded-full hover:bg-black/5 transition-colors"
                      data-testid={`admin-edit-${q.id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => remove(q.id)}
                      className="p-2 rounded-full hover:bg-red-50 text-red-500 transition-colors"
                      data-testid={`admin-delete-${q.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
          {questions.length === 0 && (
            <p className="text-muted-foreground text-center py-10">No questions in this program yet.</p>
          )}
        </div>
      </main>

      {/* Editor modal */}
      {form && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-4 overflow-y-auto py-8">
          <div className="bg-white rounded-3xl p-7 w-full max-w-lg shadow-xl my-auto" data-testid="admin-editor">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-head font-extrabold text-xl">
                {editingId ? "Edit question" : "New question"}
              </h2>
              <button onClick={() => setForm(null)} className="p-2 rounded-full hover:bg-black/5">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-semibold">Program</label>
                <select
                  value={form.module}
                  onChange={(e) => setForm({ ...form, module: e.target.value })}
                  className="mt-1 w-full rounded-2xl border border-black/10 px-4 py-3 outline-none"
                  data-testid="editor-module"
                >
                  {MODULE_LIST.map((m) => (
                    <option key={m.key} value={m.key}>
                      {m.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-sm font-semibold">Question</label>
                <textarea
                  value={form.text}
                  onChange={(e) => setForm({ ...form, text: e.target.value })}
                  rows={2}
                  className="mt-1 w-full rounded-2xl border border-black/10 px-4 py-3 outline-none"
                  data-testid="editor-text"
                />
              </div>

              <div>
                <label className="text-sm font-semibold">
                  Options <span className="text-muted-foreground font-normal">(check = correct)</span>
                </label>
                <div className="space-y-2 mt-1">
                  {form.options.map((o, i) => (
                    <div key={o.id} className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => toggleCorrect(o.id)}
                        className="w-8 h-8 rounded-full border-2 flex items-center justify-center shrink-0"
                        style={{
                          borderColor: form.correct_ids.includes(o.id) ? "#48BB78" : "rgba(0,0,0,0.15)",
                          backgroundColor: form.correct_ids.includes(o.id) ? "#48BB78" : "transparent",
                        }}
                        data-testid={`editor-correct-${o.id}`}
                      >
                        {form.correct_ids.includes(o.id) && <Check className="w-4 h-4 text-white" />}
                      </button>
                      <input
                        value={o.text}
                        onChange={(e) => {
                          const opts = [...form.options];
                          opts[i] = { ...o, text: e.target.value };
                          setForm({ ...form, options: opts });
                        }}
                        placeholder={`Option ${o.id.toUpperCase()}`}
                        className="flex-1 rounded-2xl border border-black/10 px-4 py-2.5 outline-none"
                        data-testid={`editor-option-${o.id}`}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <label className="text-sm font-semibold">Insight / explanation</label>
                  <input
                    value={form.explanation}
                    onChange={(e) => setForm({ ...form, explanation: e.target.value })}
                    className="mt-1 w-full rounded-2xl border border-black/10 px-4 py-3 outline-none"
                    data-testid="editor-explanation"
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold">Points</label>
                  <input
                    type="number"
                    value={form.points}
                    onChange={(e) => setForm({ ...form, points: parseInt(e.target.value) || 0 })}
                    className="mt-1 w-full rounded-2xl border border-black/10 px-4 py-3 outline-none"
                    data-testid="editor-points"
                  />
                </div>
              </div>
            </div>

            <button
              onClick={save}
              className="mt-6 w-full bg-[#2D3748] text-white font-semibold py-3.5 rounded-full hover:-translate-y-0.5 transition-transform"
              data-testid="editor-save"
            >
              {editingId ? "Save changes" : "Add question"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function FilterChip({ active, onClick, label, color }) {
  return (
    <button
      onClick={onClick}
      className="px-4 py-2 rounded-full text-sm font-semibold border transition-colors"
      style={{
        backgroundColor: active ? color || "#2D3748" : "white",
        color: active ? "white" : "#4A5568",
        borderColor: active ? "transparent" : "rgba(0,0,0,0.08)",
      }}
      data-testid={`filter-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}
    >
      {label}
    </button>
  );
}
